import { useSnackbar } from '@/contexts/SnackBarContext';
import { BalanceDto } from '@/models/suba/balances/BalanceDTO.model';
import { DisbursementSource } from '@/models/suba/disbursements/InitiateDisbursementRequest.model';
import { SubAccountDto } from '@/models/suba/SubAccountDTO.model';
import { DialogAccountHeader } from '@/routes/suba/common/components/DialogAccountHeader.component';
import { DisbursementService } from '@/services/suba/disbursement/Disbursement.service';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Step,
  StepContent,
  StepLabel,
  Stepper
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AccountLevel } from '@vestwell-sub-accounting/models/accountsAndLedgers/AccountLevel';
import {
  BankAccountType,
  CheckMethod,
  DisbursementCountry,
  DisbursementMethod,
  DisbursementType
} from '@vestwell-sub-accounting/models/common/DistributionRequestTypes';
import { TransactionTypeCode } from '@vestwell-sub-accounting/models/common/TransactionTypeCode';

import { AxiosError } from 'axios';
import { Form, Formik } from 'formik';
import { FC, useMemo, useState } from 'react';
import * as yup from 'yup';

import { CreateDisbursementRequestStep1 } from './CreateDisbursementRequestStep1.component';
import { CreateDisbursementRequestStep2 } from './CreateDisbursementRequestStep2.component';

export const alloweWithdrawalCodes = [
  TransactionTypeCode['HardshipDistribution'],
  TransactionTypeCode['InServiceDistribution'],
  TransactionTypeCode['RequiredMinimumDistribution'],
  TransactionTypeCode['Forfeiture'],
  TransactionTypeCode['LoanWithdrawal'],
  TransactionTypeCode['RolloverWithdrawal'],
  TransactionTypeCode['AdjustmentWithdrawal'],
  TransactionTypeCode['Withdrawal'],
  TransactionTypeCode['WithdrawalToAnotherAccount'],
  TransactionTypeCode['CashConversionWithdrawal'],
  TransactionTypeCode['CorrectiveWithdrawal'],
  TransactionTypeCode['OffsetWithdrawal'],
  TransactionTypeCode['402gDistribution'],
  TransactionTypeCode['QACADistribution'],
  TransactionTypeCode['EACADistribution'],
  TransactionTypeCode['DeathDistribution'],
  TransactionTypeCode['QDRODistribution'],
  TransactionTypeCode['ADPCorrectiveDistribution'],
  TransactionTypeCode['ACPCorrectiveDistribution'],
  TransactionTypeCode['415Distribution'],
  TransactionTypeCode['PlanDeconversionWithdrawal'],
  TransactionTypeCode['PlanTerminationWithdrawal'],
  TransactionTypeCode['DistributionToSafeHarborIRA'],
  TransactionTypeCode['CashOutDistribution'],
  TransactionTypeCode['ACHDepositReversal'],
  TransactionTypeCode['ACHReversal'],
  TransactionTypeCode['IntraPlanCashJournalOutContribution'],
  TransactionTypeCode['IntraPlanCashJournalOutConversion'],
  TransactionTypeCode['IntraPlanCashJournalOutError'],
  TransactionTypeCode['IntraPlanCashJournalOutForfeiture'],
  TransactionTypeCode['IntraPlanCashJournalOutRollover'],
  TransactionTypeCode['IntraPlanCashJournalOutSpendingBudget'],
  TransactionTypeCode['IntraPlanCashJournalOutSuspense'],
  TransactionTypeCode['ReissuedACHWithdrawal'],
  TransactionTypeCode['ReissuedCheckWithdrawal'],
  TransactionTypeCode['ReissuedWireWithdrawal'],
  TransactionTypeCode['SuspenseCashJournalOut'],
  TransactionTypeCode['WithdrawalForFeeRefund'],
  TransactionTypeCode['WithdrawalToPlanLostGains'],
  TransactionTypeCode['CheckDepositReversal'],
  TransactionTypeCode['MarketValueAdjWithdrawal'],
  TransactionTypeCode['OverpaymentWithdrawal'],
  TransactionTypeCode['WithdrawalForStudentLoanPayment'],
  TransactionTypeCode['WithdrawalFor529Contribution'],
  TransactionTypeCode['WithdrawalForESAPayment'],
  TransactionTypeCode['UnclassifiedWithdrawal'],
  TransactionTypeCode['ResidualDistribution'],
  TransactionTypeCode['ESAWithdrawal'],
  TransactionTypeCode['SDBACashJournalOut'],
  TransactionTypeCode['ReturnedContributionWithdrawal'],
  TransactionTypeCode['ExcessiveContributionAdjustment'],
  TransactionTypeCode['CorrectiveAdjustment']
];

export type CreateDisbursementRequestFormValues = {
  step1: {
    disbursementType: DisbursementType;
    subAccountId: string;
    withdrawalCode: TransactionTypeCode;
    grossAmount: string;
    ticketId: string;
    note?: string;
    subAccountBalance: BalanceDto;
  };
  step2: {
    ABARoutingNumber: string;
    payeeFederalIdentifier: string;
    accountType: BankAccountType;
    payeeType: string;
    payeeMethod: DisbursementMethod;
    checkMethod: CheckMethod;
    payeeName: string;
    payeeAddress: string;
    payeeCity: string;
    payeeState: string;
    payeeZip: string;
    payeeCountry: DisbursementCountry;
    payeeBirthDate: string;
    bankName: string;
    nameOnAccount: string;
    accountNumber: string;
    confirmAccountNumber: string;
    referenceMemo?: string;
    furtherCreditToName?: string;
    furtherCreditToAccountNumber?: string;
    overnightAccountNumber?: string;
  };
};

type CreateDisbursementRequestDialogProps = DialogProps & {
  subAccountId?: string;
  initialValues?: {
    step1?: Partial<CreateDisbursementRequestFormValues['step1']>;
    step2?: Partial<CreateDisbursementRequestFormValues['step2']>;
  };
  onSubmit?: () => void;
  onClose: () => void;
  onCancel: () => void;
  open: boolean;
  parentAccountId?: string;
  planId: string;
  subAccount?: SubAccountDto;
};

export const CreateDisbursementRequestDialog: FC<
  CreateDisbursementRequestDialogProps
> = props => {
  const { showSnackbar } = useSnackbar();

  const initiateDisbursementRequest = useMutation({
    mutationFn: (values: CreateDisbursementRequestFormValues) =>
      DisbursementService.initiate({
        accountHolderAddress: values.step2.payeeAddress,
        accountHolderBirthDate: values.step2.payeeBirthDate,
        accountHolderCity: values.step2.payeeCity,
        accountHolderFederalIdentifier: values.step2.payeeFederalIdentifier,
        accountHolderForeignCountry: values.step2.payeeCountry,
        accountHolderName: values.step2.payeeName,
        accountHolderState: values.step2.payeeState,
        accountHolderZip: values.step2.payeeZip,
        amount: String(values.step1.grossAmount),
        checkMethod: values.step2.checkMethod,
        contributionYear: new Date().getFullYear(),
        disbursementMethod: values.step2.payeeMethod,
        disbursementSource: DisbursementSource.standalone,
        disbursementType:
          values.step2.payeeMethod === DisbursementMethod.ForceOut
            ? DisbursementType.ForceOut
            : values.step1.disbursementType,
        fromId: values.step1.subAccountId,
        fromType: 'sub:subAccountId',
        furtherCreditToAccountNumber: values.step2.furtherCreditToAccountNumber,
        furtherCreditToName: values.step2.furtherCreditToName,
        overnightAccountNumber: values.step2.overnightAccountNumber,
        payeeAddress: values.step2.payeeAddress,
        payeeCity: values.step2.payeeCity,
        payeeForeignCountry: values.step2.payeeCountry,
        payeeName: values.step2.payeeName,
        payeeState: values.step2.payeeState,
        payeeZip: values.step2.payeeZip,
        reason: values.step1.note === '' ? undefined : values.step1.note,
        receivingBankAccountName: values.step2.nameOnAccount,
        receivingBankAccountNumber: values.step2.accountNumber,
        receivingBankAccountType: values.step2.accountType,
        receivingBankName: values.step2.bankName,
        receivingBankRoutingNumber: values.step2.ABARoutingNumber,
        referenceMemo:
          values.step2.referenceMemo === ''
            ? undefined
            : values.step2.referenceMemo,
        ticketId: values.step1.ticketId,
        withdrawalCode: values.step1.withdrawalCode
      }),
    mutationKey: ['DisbursementService.initiate'],
    onError: (error: AxiosError<string>) => {
      showSnackbar({
        message: error.response?.data ?? error.message,
        severity: 'error'
      });
    },
    onSuccess: () => {
      showSnackbar({
        message: 'Disbursement Request initiated',
        severity: 'success'
      });
      if (props.onSubmit) {
        props.onSubmit();
      }
    }
  });

  const [activeStep, setActiveStep] = useState(1);

  const validationSchema = useMemo(
    () =>
      yup.object({
        step1: yup.object({
          disbursementType: yup
            .string()
            .oneOf(Object.values(DisbursementType))
            .required(),
          note: yup.string().optional(),
          subAccountId: yup.string().required(),
          ticketId: yup
            .string()
            .test('is-jira-ticket-id', 'Invalid JIRA Ticket ID', value => {
              if (!value) return true;
              const regex = /^((?<!([A-Z]{1,10})-?)[A-Z]+-\d+)$/;
              return value.length <= 50 && regex.test(value);
            })
            .required(),
          withdrawalCode: yup.string().required()
        }),
        step2: yup.object({
          ABARoutingNumber: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup
              .string()
              .length(9, 'Routing Number must be 9 characters'),
            then: yup.string().when('payeeMethod', {
              is: payeeMethod => payeeMethod !== DisbursementMethod.Check,
              otherwise: yup
                .string()
                .length(9, 'Routing Number must be 9 characters'),
              then: yup
                .string()
                .required()
                .length(9, 'Routing Number must be 9 characters')
            })
          }),
          accountNumber: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().when('payeeMethod', {
              is: payeeMethod => payeeMethod !== DisbursementMethod.Check,
              otherwise: yup.string(),
              then: yup.string().required()
            })
          }),
          accountType: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().when('payeeMethod', {
              is: payeeMethod => payeeMethod !== DisbursementMethod.Check,
              otherwise: yup.string(),
              then: yup.string().required()
            })
          }),
          bankName: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().when('payeeMethod', {
              is: payeeMethod => payeeMethod !== DisbursementMethod.Check,
              otherwise: yup.string(),
              then: yup.string().required()
            })
          }),
          confirmAccountNumber: yup
            .string()
            .when('payeeType', {
              is: payeeType => payeeType !== 'sponsor',
              otherwise: yup.string(),
              then: yup.string().when('payeeMethod', {
                is: payeeMethod => payeeMethod !== DisbursementMethod.Check,
                otherwise: yup.string(),
                then: yup.string().required()
              })
            })
            .oneOf(
              [yup.ref('accountNumber'), null],
              'Account numbers must match'
            ),
          furtherCreditToAccountNumber: yup.string().optional(),
          furtherCreditToName: yup.string().optional(),
          nameOnAccount: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().when('payeeMethod', {
              is: payeeMethod => payeeMethod !== DisbursementMethod.Check,
              otherwise: yup.string(),
              then: yup.string().required()
            })
          }),
          overnightAccountNumber: yup.string().when('payeeMethod', {
            is: payeeMethod => payeeMethod === 'check',
            otherwise: yup.string(),
            then: yup.string().required()
          }),
          payeeAddress: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().required()
          }),
          payeeBirthDate: yup.string().when('payeeMethod', {
            is: payeeMethod => payeeMethod === DisbursementMethod.ForceOut,
            otherwise: yup.string(),
            then: yup.string().required()
          }),
          payeeCity: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().required()
          }),
          payeeCountry: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup
              .string()
              .oneOf(Object.values(DisbursementCountry))
              .required()
          }),
          payeeFederalIdentifier: yup
            .string()
            .length(9, 'Federal Identifier must be 9 digits')
            .matches(/^[0-9]*$/, 'Federal Identifier must be digits only')
            .required(),
          payeeMethod: yup
            .string()
            .oneOf(Object.values(DisbursementMethod))
            .required(),
          payeeName: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().required()
          }),
          payeeState: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup.string().required()
          }),
          payeeType: yup.string().required(),
          payeeZip: yup.string().when('payeeType', {
            is: payeeType => payeeType !== 'sponsor',
            otherwise: yup.string(),
            then: yup
              .string()
              .required()
              .max(9, 'Zip code must be 9 digits or less')
              .min(5, 'Zip code must be 5 digits or more')
          }),
          referenceMemo: yup.string().optional()
        })
      }),
    []
  );

  return (
    <Dialog fullWidth maxWidth='md' onClose={props.onClose} open={props.open}>
      <DialogTitle>New Disbursement Request</DialogTitle>
      <DialogAccountHeader
        accountId={props.parentAccountId}
        accountLevel={AccountLevel.ParentAccount}
      />
      <DialogContent>
        <Formik<CreateDisbursementRequestFormValues>
          initialValues={{
            step1: {
              disbursementType: DisbursementType.Partial,
              grossAmount: '',
              subAccountBalance: undefined,
              subAccountId: props.subAccount
                ? props.subAccount.subAccountId
                : props.subAccountId
                  ? props.subAccountId
                  : '',
              ticketId: '',
              withdrawalCode: TransactionTypeCode['402gDistribution'],
              ...(props.initialValues?.step1 ?? {})
            },
            step2: {
              ABARoutingNumber: '',
              accountNumber: '',
              accountType: BankAccountType.Checking,
              bankName: '',
              checkMethod: CheckMethod.Standard,
              confirmAccountNumber: '',
              nameOnAccount: '',
              payeeAddress: '',
              payeeBirthDate: '',
              payeeCity: '',
              payeeCountry: DisbursementCountry.USA,
              payeeFederalIdentifier: '',
              payeeMethod: DisbursementMethod.Ach,
              payeeName: '',
              payeeState: '',
              payeeType: 'other',
              payeeZip: '',
              ...(props.initialValues?.step2 ?? {})
            }
          }}
          onSubmit={values => initiateDisbursementRequest.mutate(values)}
          validationSchema={validationSchema}>
          <Form>
            <Stepper activeStep={activeStep - 1} orientation='vertical'>
              <Step expanded>
                <StepLabel>General</StepLabel>
                <StepContent>
                  <CreateDisbursementRequestStep1
                    active={activeStep === 1}
                    onContinue={() => setActiveStep(2)}
                    onEdit={() => setActiveStep(1)}
                    parentAccountId={props.parentAccountId}
                    planId={props.planId}
                    subAccount={props.subAccount}
                  />
                </StepContent>
              </Step>
              <Step>
                <StepLabel>Method & Payee Info</StepLabel>
                <StepContent>
                  <CreateDisbursementRequestStep2
                    loading={initiateDisbursementRequest.isLoading}
                    onBack={() => setActiveStep(1)}
                    planId={props.planId}
                  />
                </StepContent>
              </Step>
            </Stepper>
          </Form>
        </Formik>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          data-testid='cancel-button'
          disabled={initiateDisbursementRequest.isLoading}
          onClick={props.onCancel}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
